import { useMemo } from 'react';
import { type DateRange } from 'react-day-picker';

import {
  type DateRangePreset,
  DateRangePicker,
  useDateRangePresetLabels,
} from '@/components/ui/date-range-picker';
import { useLocalDateRanges } from '@/hooks/use-local-date-ranges';

import { getRangeTimeZoneLabel } from '@/utils/time-zone';

import { DateRangePanel } from '../date-range-panel';
interface DateRangeDisplayProps {
  appliedDate: DateRange;
  appliedPreset: DateRangePreset;
  onApply: (params: { preset?: DateRangePreset; range: DateRange }) => void;
  /** True while a newly-applied range/preset is still being fetched. */
  isPending?: boolean;
  /** The opening's IANA zone; when set, the panel labels the dates with it. */
  timeZone?: string;
}

export function DateRangeDisplay({
  appliedDate,
  appliedPreset,
  onApply,
  isPending,
  timeZone,
}: DateRangeDisplayProps) {
  const presetLabels = useDateRangePresetLabels();
  const localDays = useLocalDateRanges();
  // A long range scans one day at a time, so the label is kept until a bound or the
  // zone changes. Lifetime has no applied bounds; today names the zone then.
  const fromTime = appliedDate.from?.getTime();
  const toTime = appliedDate.to?.getTime();
  const timeZoneLabel = useMemo(
    () =>
      timeZone
        ? getRangeTimeZoneLabel(
            fromTime === undefined ? new Date() : new Date(fromTime),
            toTime === undefined ? undefined : new Date(toTime),
            timeZone,
          )
        : null,
    [fromTime, toTime, timeZone],
  );
  const presetSubtitle =
    appliedPreset !== 'custom' ? presetLabels[appliedPreset] : null;

  return (
    <DateRangePanel subtitle={presetSubtitle} timeZoneLabel={timeZoneLabel}>
      <div className="flex items-center rounded-lg p-2 outline-1 outline-offset-[-1px] outline-neutral-200">
        <DateRangePicker
          selectedDate={appliedDate}
          setSelectedDate={() => {}}
          selectedPreset={appliedPreset}
          onPresetChange={() => {}}
          onApply={onApply}
          showPresetInTrigger={false}
          isLoading={isPending}
          localDays={localDays}
          className="text-fx-neutral-900 [&_svg]:text-fx-neutral-600 h-auto gap-1 p-0! font-medium hover:bg-transparent"
        />
      </div>
    </DateRangePanel>
  );
}
